/**
 * Per-KR check-in staleness.
 *
 * Surfaces "how long has this Key Result been quiet" on the Weekly Check-in form
 * so a manager/owner can spot a KR that has gone without narrative. Pure and
 * time-injectable (`now`) for deterministic tests.
 */
import type { CheckIn } from '../types/okr';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Minimum quiet duration before we surface a "· N weeks" duration. One missed
 *  weekly check-in is normal; two weeks starts to matter. */
const STALE_THRESHOLD_WEEKS = 2;

/** A KR is "noted" in a check-in when it has a note row with any non-blank field. */
const krWasNoted = (checkIn: CheckIn, keyResultId: string): boolean =>
  !!checkIn.keyResultNotes?.some(
    (n) =>
      n.keyResultId === keyResultId &&
      (!!n.accomplishments?.trim() || !!n.challenges?.trim() || !!n.nextSteps?.trim())
  );

/**
 * Human "N weeks" staleness label for a KR's collapsed check-in card, or
 * `undefined` when it should not be shown.
 *
 * Reference point (within the `checkIns` window passed in — callers should fetch
 * enough history to cover a full check-in cycle):
 * - the most recent check-in that NOTED this KR; or
 * - if the KR was never noted, the OLDEST check-in in the window (chronic neglect).
 *
 * Returns `undefined` when there is no check-in history, or the gap is under the
 * 2-week threshold. The card itself only renders the label while it is empty.
 */
export const computeKRStaleLabel = (
  checkIns: CheckIn[] | undefined,
  keyResultId: string,
  now: number = Date.now()
): string | undefined => {
  if (!checkIns?.length) return undefined;

  const toTime = (c: CheckIn) => new Date(c.checkInDate).getTime();
  const allDates = checkIns.map(toTime).filter((t) => !Number.isNaN(t));
  if (!allDates.length) return undefined;

  const notedDates = checkIns
    .filter((c) => krWasNoted(c, keyResultId))
    .map(toTime)
    .filter((t) => !Number.isNaN(t));

  const reference = notedDates.length
    ? Math.max(...notedDates) // most recent time this KR was noted
    : Math.min(...allDates); // never noted -> oldest available check-in

  const weeks = Math.floor((now - reference) / WEEK_MS);
  if (weeks < STALE_THRESHOLD_WEEKS) return undefined;
  return `${weeks} weeks`;
};
